import { useEffect, useRef } from 'react';

interface AudioVisualizerProps {
 stream: MediaStream | null;
 isRecording: boolean;
 className?: string;
 color?: string;
}

export function AudioVisualizer({
 stream,
 isRecording,
 className = '',
 color,
}: AudioVisualizerProps) {
 const canvasRef = useRef<HTMLCanvasElement>(null);
 const animationRef = useRef<number | null>(null);
 const analyserRef = useRef<AnalyserNode | null>(null);
 const dataArrayRef = useRef<Uint8Array | null>(null);

 useEffect(() => {
  if (!stream || !isRecording || !canvasRef.current) return;

  const audioContext = new (
   window.AudioContext || (window as any).webkitAudioContext
  )();
  const source = audioContext.createMediaStreamSource(stream);
  const analyser = audioContext.createAnalyser();

  analyser.fftSize = 64; // Low resolution for bars
  source.connect(analyser);

  const bufferLength = analyser.frequencyBinCount;
  const dataArray = new Uint8Array(bufferLength);

  analyserRef.current = analyser;
  dataArrayRef.current = dataArray;

  const canvas = canvasRef.current;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const draw = () => {
   if (!isRecording) return;

   animationRef.current = requestAnimationFrame(draw);

   analyser.getByteFrequencyData(dataArray);

   ctx.clearRect(0, 0, canvas.width, canvas.height);

   const barWidth = (canvas.width / bufferLength) * 2.5;
   let barHeight;
   let x = 0;

   for (let i = 0; i < bufferLength; i++) {
    barHeight = (dataArray[i] / 255) * canvas.height;

    // Use custom color or default gradient
    if (color) {
     ctx.fillStyle = color;
    } else {
     // Gradient or Color based on volume
     const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
     gradient.addColorStop(0, '#22c55e'); // Green
     gradient.addColorStop(1, '#15803d');
     ctx.fillStyle = gradient;
    }

    // Center bars vertically
    const y = (canvas.height - barHeight) / 2;
    // Rounded bars?
    ctx.fillRect(x, y, barWidth, barHeight);

    x += barWidth + 2;
   }
  };

  draw();

  return () => {
   if (animationRef.current) {
    cancelAnimationFrame(animationRef.current);
   }
   if (audioContext.state !== 'closed') {
    audioContext.close();
   }
  };
 }, [stream, isRecording]);

 return (
  <canvas
   ref={canvasRef}
   className={`w-full h-full ${className}`}
   width={200}
   height={60}
  />
 );
}
